import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { handleKeyboardActivation } from '../../shared/keyboard';
import styles from './Select.css';

const cx = classnames.bind(styles);

function Select({ className, options, onChange, value, errorText, name, renderOption, label }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleOutsideClick = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(name, option);
        setIsOpen(false);
    };

    const handleOpen = () => {
        setIsOpen(!isOpen);

        if (!value) {
            onChange(name, '');
        }
    };

    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });

    return (
        <div className={classes} ref={rootRef}>
            {Boolean(label) && <label htmlFor={name}>{label}:</label>}
            <div
                className={cx({ select: true, open: isOpen, selected: Boolean(value), error: Boolean(errorText) })}
                onClick={handleOpen}
                onKeyDown={handleKeyboardActivation(handleOpen)}
                tabIndex='0'
                aria-expanded={isOpen}
                aria-haspopup='listbox'
                role='button'
            >
                {value || t('common.selectAnOption')}
                {Boolean(errorText) && !isOpen ? <Icon name='alertTriangle' /> : <Icon name='chevronDownSmall' />}
            </div>
            {isOpen && (
                <ul className={styles.options} role='listbox'>
                    {options.map((option) => {
                        if (renderOption) return renderOption(option, () => setIsOpen(false));

                        return (
                            <li
                                key={option}
                                onClick={() => handleSelect(option)}
                                onKeyDown={handleKeyboardActivation(() => handleSelect(option))}
                                tabIndex='0'
                                role='option'
                                aria-selected={option === value}
                                className={option === value ? styles.selected : ''}
                            >
                                {option}
                            </li>
                        );
                    })}
                </ul>
            )}
            {Boolean(errorText) && !isOpen && <p className={styles.alert}>{errorText}.</p>}
        </div>
    );
}

Select.propTypes = {
    className: PropTypes.string,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    errorText: PropTypes.string,
    renderOption: PropTypes.func,
    name: PropTypes.string.isRequired,
    label: PropTypes.string
};

Select.defaultProps = {
    className: '',
    errorText: '',
    label: ''
};

export {
    Select
};
